import type { IWidget } from "app/components/widget/widget-types/Widget";
import type { IDataFetcher, IDataFetcherFactory } from "app/components/widget/widget-fetchers/IDataFetcher";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import type {
    IData,
    IDataRecord,
    ISitesData,
} from "app/common/keyword-research/website-keywords/websiteKeywordsPageTypes";
import DurationService from "app/services/DurationService";
import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import { FILTER_DEFAULT_SEPARATOR } from "app/pages/website-analysis/constants/constants";
import allSearchTypes from "app/pages/website-analysis/constants/searchTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import type { IRule } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/types";
import Sentry from "@similarweb/sentry-logger";

const SERVER_API = "/widgetApi/WebsiteAnalysisV2/WebsiteAnalysisOrganic/Table";

class WebsiteKeywordsDataFetcher<T> implements IDataFetcher<T> {
    protected widget: IWidget<T>;
    constructor(widget) {
        this.widget = widget;
    }
    fetch(apiParams = this.widget.apiParams as any) {
        const {
            multiIncludeExcludeKeywords: multiIncludeExcludeKeywordsString,
            multiIncludeExcludeWebsites: multiIncludeExcludeWebsitesString,
            pageFilter,
            source,
            family,
            trafficPreset,
            ...params
        } = apiParams;

        // transform 1.0 filter values to 2.0
        const searchEngines = family?.length
            ? family.split(FILTER_DEFAULT_SEPARATOR).map((item) => item.replace(/["\\]/g, ""))
            : undefined;
        const searchTypes = source?.length
            ? source.split(FILTER_DEFAULT_SEPARATOR).map((item) => allSearchTypes.find((st) => st.id === item).value)
            : undefined;

        const [sort] = params.orderBy?.split?.(" ") ?? ["Share"];

        const _params = {
            ...params,
            pageFilterJSON: pageFilter,
            page: 1,
            pageSize: 100,
            sort,
            asc: false,
            searchTypes,
            searchEngines,
            Preset: trafficPreset,
            multiIncludeExcludeKeywords: multiIncludeExcludeKeywordsString,
            multiIncludeExcludeWebsites: multiIncludeExcludeWebsitesString,
        };
        delete _params.key;

        const paramsAsString = requestParams(_params);
        try {
            const multiIncludeExcludeWebsitesParsed: IRule[] = multiIncludeExcludeWebsitesString
                ? JSON.parse(multiIncludeExcludeWebsitesString)
                : [];
            const multiIncludeExcludeKeywordsParsed: IRule[] = multiIncludeExcludeKeywordsString
                ? JSON.parse(multiIncludeExcludeKeywordsString)
                : [];
            return DefaultFetchService.getInstance()
                .post(`${SERVER_API}?${paramsAsString}`, [
                    ...multiIncludeExcludeKeywordsParsed.map((rule) => ({
                        ...rule,
                        field: "keyword",
                    })),
                    ...multiIncludeExcludeWebsitesParsed.map((rule) => ({
                        ...rule,
                        field: "website",
                    })),
                ])
                .then(this.transformData(params.keys.split(","))) as any;
        } catch (e) {
            Sentry.error("Error parsing JSON strings:", e);
        }
    }

    transformData = (keys: string[]) => (data: IData) => {
        const siteToColorMap = keys.reduce((agr, cur, index) => {
            agr[cur] = CHART_COLORS.compareMainColors[index];
            return agr;
        }, {});
        const transformedData = data.Data?.Records?.map((dataUnit: IDataRecord) => {
            const getExtractedDataFromSitesData = (): [Record<string, string[]>, Record<string, number>] => {
                if (!Array.isArray(dataUnit.SitesData)) {
                    return [{}, {}];
                }
                const siteSerpFeatures = {};
                const siteOrigins = {};
                dataUnit.SitesData.forEach((site: ISitesData) => {
                    siteSerpFeatures[site.Site] = site.SerpFeatures;
                    siteOrigins[site.Site] = site.Share;
                });
                return [siteSerpFeatures, siteOrigins];
            };
            const extractedSitesData = getExtractedDataFromSitesData();
            const shareOfDesktop = dataUnit.DesktopClicks / dataUnit.Clicks;
            const organicClicksShare = dataUnit.OrganicClicks / dataUnit.Clicks;

            const durationText = DurationService.getFormattedDateOutOfDurationParam(
                this.widget.apiParams.duration,
                shortMonthAndYear,
            );

            const sitesData = [...dataUnit.SitesData];
            sitesData.sort((a, b) => ((a.Position ?? Infinity) < (b.Position ?? Infinity) ? -1 : 1));

            return {
                ...dataUnit,
                ClicksChange: {
                    first: dataUnit.PreviousClicks,
                    last: dataUnit.Clicks,
                    change: dataUnit.Change,
                    changeState: dataUnit.ChangeState,
                },
                VolumeChange: {
                    first: dataUnit?.KwWindowVolume,
                    last: dataUnit?.PreviousVolume,
                    change: dataUnit?.VolumeChange,
                    changeState: dataUnit?.VolumeChangeState,
                },
                SearchTerm: dataUnit.Keyword,
                OrganicClicksShare: organicClicksShare,
                WebSourceShare: shareOfDesktop,
                SerpScrapeDate: dataUnit.LatestKeywordSerpDate,
                SiteSerpFeatures: extractedSitesData[0],
                SiteOrigins: extractedSitesData[1],
                Position: sitesData.map((site: ISitesData) => {
                    return {
                        Key: site.Site,
                        Value: site.Position ?? -1,
                        Color: siteToColorMap[site.Site],
                    };
                }),
                Intent: {
                    PrimaryIntent: dataUnit.PrimaryIntent,
                    SecondaryIntent: dataUnit.SecondaryIntent,
                },
                durationText,
            };
        });
        return {
            ...data,
            Data: transformedData,
        };
    };

    destroy() {
        return undefined;
    }
}

const WebsiteKeywordsDataFetcherFactory: IDataFetcherFactory<any> = {
    create: function (widget) {
        return new WebsiteKeywordsDataFetcher<any>(widget);
    },
};

export default WebsiteKeywordsDataFetcherFactory;
